import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import classNames from "classnames";
import { SKUS_QUERY_STRING } from "../../../../../constants/skus";
import ImagePlaceholder from "../../../../UI/ImagePlaceholder/ImagePlaceholder";
import Loading from "../../../../UI/Loading/Loading";
import EmptyContent from "../../../../UI/EmptyContent/EmptyContent";
import PrimaryButton from "../../../../UI/PrimaryButton/PrimaryButton";
import ClearBtn from "../../../../UI/ClearBtn/ClearBtn";
import searchIcon from "../../../../../assets/icons/search.svg";
import classes from "./SkusFilterDropdownMenu.module.css";

const SkusFilterDropdownMenu = (props) => {
  const {
    menu,
    isLoading,
    error,
    close,
    queryStringName,
    menuItemClassName,
    searchClassName,
  } = props;

  const [searchParams, setSearchParams] = useSearchParams();
  const queryStringData =
    "id" in (menu[0] || {})
      ? searchParams
          .get(queryStringName)
          ?.split(",")
          ?.map((value) => Number(value)) || []
      : searchParams.get(queryStringName)?.split(",") || [];

  const [searchText, setSearchText] = useState("");
  const [filteredMenu, setFilteredMenu] = useState(menu);
  const [checkedItems, setCheckedItems] = useState(queryStringData);

  const searchTextChangedHandler = (e) => {
    const {
      target: { value },
    } = e;
    setSearchText(value);
  };

  const menuItemToggleHandler = (item) => {
    const itemValue = "id" in item ? item.id : item.name;
    setCheckedItems((prevCheckedItems) => {
      if (prevCheckedItems.includes(itemValue)) {
        return prevCheckedItems.filter((item) => item !== itemValue);
      } else {
        return [...prevCheckedItems, itemValue];
      }
    });
  };

  const isAnyItemChecked = () => {
    return checkedItems.length > 0;
  };

  const submitFiltersHandler = () => {
    searchParams.set(queryStringName, checkedItems.join());
    setSearchParams(searchParams);
    close();
  };

  const clearFiltersHandler = () => {
    searchParams.delete(queryStringName);
    if (queryStringName === SKUS_QUERY_STRING.CAR_BRANDS) {
      searchParams.delete(SKUS_QUERY_STRING.CAR_MODELS);
      searchParams.delete(SKUS_QUERY_STRING.CAR_TRANSMISSION_CC);
    } else if (queryStringName === SKUS_QUERY_STRING.CAR_MODELS) {
      searchParams.delete(SKUS_QUERY_STRING.CAR_TRANSMISSION_CC);
    }
    setSearchParams(searchParams);
    close();
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchText.trim() === "") {
        setFilteredMenu(menu);
      } else {
        setFilteredMenu(
          menu.filter(
            (item) =>
              item.name.toLowerCase().includes(searchText.toLowerCase()) ||
              (item.englishName &&
                item.englishName
                  .toLowerCase()
                  .includes(searchText.toLowerCase()))
          )
        );
      }
    }, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchText, menu]);

  return (
    <div className={classes["filter-menu"]}>
      {isLoading ? (
        <Loading className={classes["filter-menu__loading"]} />
      ) : error ? (
        <EmptyContent
          message={error}
          className={classes["filter-menu__empty"]}
        />
      ) : (
        <>
          <div className={classes["filter-menu__search-wrapper"]}>
            <img
              src={searchIcon}
              alt="search"
              className={classes["filter-menu__search-icon"]}
            />
            <input
              className={classNames(
                classes["filter-menu__search-input"],
                searchClassName
              )}
              placeholder="بحث"
              value={searchText}
              onChange={searchTextChangedHandler}
            />
          </div>
          {filteredMenu.length === 0 ? (
            <EmptyContent
              className={classes["filter-menu__empty"]}
              message="لا يوجد نتائج !"
            />
          ) : (
            <>
              <ul
                className={classNames(
                  classes["filter-menu__list"],
                  menuItemClassName
                )}
              >
                {filteredMenu.map((item, index) => (
                  <li key={index} className={classes["filter-menu__list-item"]}>
                    <input
                      type="checkbox"
                      id={`${index}_${item.name}`}
                      checked={
                        "id" in item
                          ? checkedItems.includes(item.id)
                          : checkedItems.includes(item.name)
                      }
                      onChange={() => menuItemToggleHandler(item)}
                    />
                    <label
                      htmlFor={`${index}_${item.name}`}
                      className={classes["filter-menu__list-item-label"]}
                    >
                      {"imageUrl" in item && (
                        <ImagePlaceholder
                          src={item.imageUrl || ""}
                          alt={item.name}
                          className={
                            classes["filter-menu__list-item-label-img"]
                          }
                        />
                      )}
                      {item.name}
                    </label>
                  </li>
                ))}
              </ul>
              <div className={classes["filter-menu__actions"]}>
                <PrimaryButton
                  className={classes["filter-menu__filter-btn"]}
                  onClick={submitFiltersHandler}
                  disabled={!isAnyItemChecked()}
                >
                  اظهار النتائج
                </PrimaryButton>
                <ClearBtn
                  className={classes["filter-menu__clear-btn"]}
                  onClick={clearFiltersHandler}
                  disabled={queryStringData.length === 0}
                >
                  مسح الكل
                </ClearBtn>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default SkusFilterDropdownMenu;

SkusFilterDropdownMenu.propTypes = {
  menu: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      imageUrl: PropTypes.string,
    })
  ),
  menuItemClassName: PropTypes.string,
  searchClassName: PropTypes.string,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  close: PropTypes.func,
  queryStringName: PropTypes.string,
};
